import React, { useCallback, useRef } from "react";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Stack, Typography } from "@mui/material";
import { Menu } from "components/popups/Menu/Menu";
import { Rename } from "components/Prompts";
import { useActionHook, usePrompt } from "hooks";
import { useDealActions } from "store";
import { ITemplate } from "store/types";

interface TemplateSelectorProps {
  templates: ITemplate[];
  selectedTemplateId: number | null;
  onSelect: (id: number | null) => void;
  onTemplatesChanged: React.Dispatch<React.SetStateAction<ITemplate[] | undefined>>;
  assetClassId?: number | null;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onSelect,
  onTemplatesChanged,
  assetClassId
}) => {
  const { deleteTemplateAction } = useDealActions();

  const handleDelete = useCallback(
    async (templateId: number) => {
      await deleteTemplateAction(templateId);
      onTemplatesChanged((prev) => {
        if (!prev) return [];
        return prev.filter((t) => t.id !== templateId);
      });
      if (selectedTemplateId === templateId) {
        onSelect(null);
      }
    },
    [deleteTemplateAction, onTemplatesChanged, selectedTemplateId, onSelect]
  );

  const filtered = assetClassId
    ? templates.filter((t) => !t.asset_class_id || t.asset_class_id === assetClassId)
    : templates;

  return (
    <Stack
      sx={{
        bgcolor: "#f6f8fc",
        borderRadius: "6px",
        padding: "16px",
        overflow: "hidden",
        minHeight: "190px"
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: "14px",
          overflowX: "auto",
          padding: "2px",
          "&::-webkit-scrollbar": { height: 7, bgcolor: "rgba(196,196,196,0.57)" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#c4c4c4" }
        }}
      >
        {filtered.map((template) => (
          <TemplateSelectorCard
            key={template.id}
            template={template}
            isSelected={template.id === selectedTemplateId}
            onClick={() => onSelect(template.id)}
            onDelete={() => handleDelete(template.id)}
            onTemplatesChanged={onTemplatesChanged}
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface TemplateSelectorCardProps {
  template: ITemplate;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  onTemplatesChanged: React.Dispatch<React.SetStateAction<ITemplate[] | undefined>>;
}

const TemplateSelectorCard: React.FC<TemplateSelectorCardProps> = ({
  template,
  isSelected,
  onClick,
  onDelete,
  onTemplatesChanged
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const openPrompt = usePrompt();
  const { updateTemplateAction } = useDealActions();

  const { action: updateTemplate } = useActionHook(updateTemplateAction, {
    onSuccess: (res) => {
      onTemplatesChanged((prev) => {
        if (!prev?.length) return [];
        return prev.map((t) => (t.id === res.id ? res : t));
      });
    }
  });

  const handleRename = useCallback(async () => {
    await openPrompt({
      Component: Rename,
      componentType: "popover",
      anchorEl: containerRef.current as HTMLElement,
      anchorOrigin: { vertical: "top", horizontal: "right" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      props: {
        title: template.title,
        cb: async (value: string) => {
          await updateTemplate({ templateId: template.id, title: value });
        }
      }
    });
  }, [openPrompt, template.id, template.title, updateTemplate]);

  const handleMarkForAI = useCallback(async () => {
    await updateTemplate({ templateId: template.id, is_ai: !template.is_ai });
  }, [template.id, template.is_ai, updateTemplate]);

  return (
    <Stack
      ref={containerRef}
      onClick={onClick}
      sx={{
        position: "relative",
        width: 130,
        height: 150,
        flexShrink: 0,
        bgcolor: "white",
        borderRadius: "8px",
        padding: "10px",
        cursor: "pointer",
        border: "2px solid",
        borderColor: isSelected ? "#071f35" : "transparent",
        boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
        justifyContent: "space-between",
        "&:hover": {
          boxShadow: "0px 2px 8px 0px rgba(18,18,23,0.16), 0px 1px 4px 0px rgba(18,18,23,0.1)"
        }
      }}
    >
      {/* Top: icon + menu */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ overflow: "hidden", flex: 1 }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 24, color: "#071f35", opacity: 0.6, marginTop: "4px" }} />
        <Menu
          iconButtonProps={{
            sx: { padding: "4px" }
          }}
          options={[
            {
              title: "Rename",
              onClick: handleRename
            },
            {
              title: template.is_ai ? "Unmark for AI" : "Mark for AI",
              onClick: handleMarkForAI
            },
            {
              title: "Delete",
              onClick: onDelete
            }
          ]}
        />
      </Stack>

      {/* Bottom: title */}
      <Typography sx={{ fontSize: "16px", fontWeight: 700, lineHeight: "24px", color: "#071f35" }}>
        {template.title}
      </Typography>
    </Stack>
  );
};
